import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, HardHat, Building2, Ruler, Clock, CheckCircle2, Hammer, Trees, Shield } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

// ─── BUILDER HERO ───
export function BuilderHero() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'hero');
  const c = {
    subheading: "Building Tomorrow's Landmarks",
    heading: "Crafting Iconic Spaces.",
    description: "From vision to reality — delivering world-class residential and commercial developments.",
    buttonText: "Explore Projects",
    buttonLink: "/projects",
    buttonText2: "Download Brochure",
    buttonLink2: "/contact",
    imageUrl: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1920&q=80",
    stats: [{ value: "25+", label: "Projects" }, { value: "10M+", label: "Sq.Ft." }, { value: "15K+", label: "Homeowners" }],
    ...content,
  };
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-primary">
      <div className="max-w-[1400px] mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 rounded mb-6">
            <HardHat className="w-4 h-4 text-accent" />
            <span className="text-accent text-xs font-bold uppercase tracking-wider">{c.subheading}</span>
          </div>
          <h1 className="font-sans text-4xl md:text-6xl font-bold text-primary-foreground leading-tight mb-6">
            {c.heading}
          </h1>
          <p className="text-primary-foreground/70 text-lg mb-8 max-w-lg">{c.description}</p>
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <Link to={c.buttonLink || "/projects"} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-accent-foreground font-bold text-sm uppercase rounded hover:bg-accent/90 transition-all">
              {c.buttonText} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={c.buttonLink2 || "/contact"} className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-primary-foreground/30 text-primary-foreground font-bold text-sm uppercase rounded hover:bg-primary-foreground/10 transition-all">
              {c.buttonText2 || "Download Brochure"}
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {(c.stats || []).map((s: any) => (
              <div key={s.label} className="border-l-2 border-accent pl-4">
                <div className="text-2xl font-bold text-primary-foreground">{s.value}</div>
                <div className="text-primary-foreground/50 text-xs uppercase">{s.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="relative hidden lg:block">
          <div className="relative rounded-lg overflow-hidden shadow-2xl">
            <img src={c.imageUrl} alt="Construction" className="w-full h-[500px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/50 to-transparent" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── BUILDER ONGOING PROJECTS ───
export function BuilderOngoingProjects() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'ongoing-projects');
  const c = {
    heading: "Ongoing Projects",
    subheading: "Our Developments",
    description: "Explore our latest developments.",
    items: [
      { title: "Skyline Residences", description: "Luxury Apartments · 320 Units · 75% Complete · Dec 2025", imageUrl: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80" },
      { title: "Green Valley Township", description: "Integrated Township · 1200 Plots · 45% Complete · Mar 2026", imageUrl: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80" },
      { title: "Metro Business Hub", description: "Commercial Complex · 150 Offices · 90% Complete · Jun 2025", imageUrl: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80" },
    ],
    ...content,
  };
  return (
    <section className="py-20 bg-secondary">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <span className="text-accent font-bold text-sm uppercase tracking-wider">{c.subheading}</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-3">{c.heading}</h2>
          {c.description && <p className="text-muted-foreground mt-2">{c.description}</p>}
        </motion.div>
        <div className="space-y-6">
          {(c.items || []).map((p: any, i: number) => (
            <motion.div key={i} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
              className="bg-card rounded-lg overflow-hidden shadow-md flex flex-col md:flex-row group cursor-pointer hover:shadow-xl transition-shadow">
              <div className="md:w-72 h-48 md:h-auto overflow-hidden flex-shrink-0">
                <img src={p.imageUrl || '/placeholder.svg'} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="flex-1 p-6">
                <h3 className="text-xl font-bold mb-3 group-hover:text-accent transition-colors">{p.title}</h3>
                <p className="text-muted-foreground text-sm">{p.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BUILDER AMENITIES ───
export function BuilderAmenities() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'builder-amenities');
  const c = {
    heading: "World-Class Amenities",
    subheading: "Amenities",
    description: "Every project designed for modern living.",
    items: [
      { title: "Clubhouse", description: "30,000 sq.ft. world-class clubhouse" },
      { title: "Landscaped Gardens", description: "Acres of manicured green spaces" },
      { title: "24/7 Security", description: "Gated community with CCTV surveillance" },
      { title: "Sports Complex", description: "Tennis, basketball, swimming pool" },
      { title: "Premium Finishes", description: "Italian marble and imported fittings" },
      { title: "RERA Approved", description: "All projects fully compliant" },
    ],
    ...content,
  };
  const defaultIcons = [Building2, Trees, Shield, Ruler, Hammer, CheckCircle2];
  return (
    <section className="py-20 bg-card">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{c.heading}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">{c.description}</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(c.items || []).map((a: any, i: number) => {
            const Icon = defaultIcons[i % defaultIcons.length];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="p-6 border border-border rounded-lg hover:-translate-y-1 transition-transform duration-300 hover:shadow-lg hover:border-accent/50">
                <div className="w-12 h-12 rounded bg-accent/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-bold text-lg mb-2">{a.title}</h3>
                <p className="text-muted-foreground text-sm">{a.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── BUILDER CONSTRUCTION PROGRESS ───
export function BuilderConstructionProgress() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'construction-progress');
  const c = {
    heading: "Construction Updates",
    subheading: "Progress",
    description: "Track real-time progress on our projects.",
    items: [
      { title: "Phase 1 — Completed", description: "Foundation & structure work" },
      { title: "Phase 2 — In Progress", description: "Interior finishing & MEP" },
      { title: "Phase 3 — Upcoming", description: "Landscaping & amenities" },
      { title: "Phase 4 — Planned", description: "Handover & possession" },
    ],
    ...content,
  };
  return (
    <section className="py-20 bg-primary text-primary-foreground">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{c.heading}</h2>
          <p className="text-primary-foreground/60">{c.description}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {(c.items || []).map((p: any, i: number) => {
            const done = p.title.toLowerCase().includes('completed');
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className="relative">
                <div className={`p-6 rounded-lg border ${done ? 'border-accent bg-accent/10' : 'border-primary-foreground/20'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-4 ${done ? 'bg-accent text-accent-foreground' : 'bg-primary-foreground/10'}`}>
                    {done ? <CheckCircle2 className="w-5 h-5" /> : <span className="text-sm font-bold">{i + 1}</span>}
                  </div>
                  <h3 className="font-bold text-lg mb-1">{p.title}</h3>
                  <p className="text-primary-foreground/60 text-sm mt-2">{p.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
